// ============================================================
// ENTROPY - randomness for things that are NOT dice.
//
// `Math.random()` belongs to the dice and to nothing else. The E2E
// harness stubs it with a seeded sequence (`setDiceSequence` in
// main.jsx) and that stub CYCLES past the end, so any other consumer
// does not fail loudly - it silently shifts every subsequent roll and
// returns a plausible wrong number from earlier in the sequence.
//
// That cost two real diagnoses in one day: an id minted in
// `useApplyHits` shifted every roll after a damage application, and the
// retry jitter below shifted rolls only in loaded runs, which made the
// same test pass alone and fail in a full sweep.
//
// So: ids come from monotonic counters (see engine/roster/factories.ts),
// and anything that genuinely needs entropy comes from HERE, which uses
// `crypto` and never touches Math.random.
// ============================================================

/** Fill from crypto when it exists - every browser and Node ≥19 has it. */
function cryptoBytes(n: number): Uint8Array | null {
  const c = globalThis.crypto;
  if (!c?.getRandomValues) return null;
  return c.getRandomValues(new Uint8Array(n));
}

/**
 * A fraction in [0, 1), like Math.random() but without stealing a die.
 *
 * Falls back to a fixed 0.5 rather than to Math.random() when crypto is
 * unavailable: a constant is obviously wrong and easy to spot, whereas
 * quietly reaching for Math.random() is the exact bug this module exists
 * to prevent.
 */
export function randomFraction(): number {
  const bytes = cryptoBytes(4);
  if (!bytes) return 0.5;
  return ((bytes[0] << 24) >>> 0) / 0x100000000 + bytes[1] / 0x1000000;
}

/**
 * A short uppercase token, for join codes and the like.
 *
 * Crypto-backed, which a join code should be anyway - `Math.random()` was
 * never a good source for something people are meant not to guess.
 */
export function randomToken(length = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = cryptoBytes(length);
  if (!bytes) return alphabet.slice(0, length);
  let out = '';
  for (let i = 0; i < length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}
